import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import { getWorldCurrencies } from "../../../../utils/currency";
import CurrencyFlag from "./CurrencyFlag";
import "./CurrencySelect.css";

export default function CurrencySelect({
  value = "USD",
  onChange,
  allowEmpty = false,
  emptyLabel,
  excludeCodes = [],
}) {
  const { t, i18n } = useTranslation();
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelStyle, setPanelStyle] = useState({});
  const excludeKey = (excludeCodes || []).join(",");
  const selectedCode = String(value || "").toUpperCase();

  const currencies = useMemo(() => {
    const excluded = new Set(
      excludeKey
        .split(",")
        .map((code) => code.toUpperCase())
        .filter(Boolean)
    );
    return getWorldCurrencies(i18n.language || "en").filter(
      (item) => !excluded.has(item.code)
    );
  }, [i18n.language, excludeKey]);

  const selected = selectedCode
    ? currencies.find((item) => item.code === selectedCode) || {
        code: selectedCode,
        name: selectedCode,
        symbol: selectedCode,
      }
    : {
        code: "",
        name: emptyLabel || t("currencySecondaryNone"),
        symbol: "—",
      };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return currencies;
    return currencies.filter((item) => item.search.includes(needle));
  }, [currencies, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const updatePanelPosition = () => {
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, 280);
    const gap = 6;
    const maxHeight = Math.min(360, window.innerHeight - 24);
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - width - 8
    );

    setPanelStyle({
      position: "fixed",
      left,
      width,
      maxHeight,
      zIndex: 4000,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap, top: "auto" }
        : { top: rect.bottom + gap, bottom: "auto" }),
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, filtered.length, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const inTrigger = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inTrigger && !inPanel) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(timer);
    };
  }, [open]);

  const selectCurrency = (code) => {
    onChange?.(code);
    close();
  };

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className="currency-select__panel"
          style={panelStyle}
        >
          <div className="currency-select__search">
            <FaSearch aria-hidden />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("currencySearchPlaceholder")}
              aria-label={t("currencySearchPlaceholder")}
            />
          </div>
          <div className="currency-select__count">
            {t("currencyFilterCount", { count: filtered.length })}
          </div>
          <ul className="currency-select__list" role="listbox">
            {allowEmpty ? (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={!selectedCode}
                  className={!selectedCode ? "is-selected" : ""}
                  onClick={() => selectCurrency("")}
                >
                  <span className="currency-select__symbol">—</span>
                  <span className="currency-select__copy">
                    <strong>{emptyLabel || t("currencySecondaryNone")}</strong>
                    <span>{t("optional")}</span>
                  </span>
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="currency-select__empty">{t("currencyNoResults")}</li>
            ) : (
              filtered.map((item) => {
                const active = item.code === selected.code;
                return (
                  <li key={item.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={active ? "is-selected" : ""}
                      onClick={() => selectCurrency(item.code)}
                    >
                      <CurrencyFlag code={item.code} />
                      <span className="currency-select__symbol">{item.symbol}</span>
                      <span className="currency-select__copy">
                        <strong>{item.code}</strong>
                        <span>{item.name}</span>
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="currency-select" ref={rootRef}>
      <button
        type="button"
        className={`currency-select__trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <CurrencyFlag code={selected.code} />
        <span className="currency-select__symbol">{selected.symbol}</span>
        <span className="currency-select__copy">
          <strong>{selected.code}</strong>
          <span>{selected.name}</span>
        </span>
        <FaChevronDown className="currency-select__chevron" />
      </button>
      {panel}
    </div>
  );
}
